<template>
  <div>
    <div id="google_translate_element"></div>
    <div v-if="translateType === 'auto'" class="poweredby">
      <a class="goog-logo-link" href="https://translate.google.com" target="_blank">
        <img src="~@/assets/img/logo_google_translate.png" alt="powered by google translate" />
      </a>
    </div>

  </div>
</template>

<script>
export default {
  name: 'Translate',
  props: [
    'translateType'
  ],

  computed: {
    // 라우터가 정한 최종 언어 (예: 'ko','en','ja','zh-CN','zh-TW' …)
    ln () {
      return this.$route.params.ln
    },
    // 시스템 기본 언어(수동 제공 언어 출발점). 필요 시 App 규칙으로 계산한 baseLang으로 바꿔도 OK
    defaultLang () {
      return this.$store.getters['common/getSetting'].lang.default || 'ko'
    }
  },

  watch: {
    ln: {
      immediate: true,
      handler() {
        this.applyTranslateRule()
      }
    },
    translateType: {
      immediate: true,
      handler() {
        this.applyTranslateRule()
      }
    }
  },

  async mounted() {
    // 사용하는 언어가 모두 직접번역이 아닐 때 인스턴스 생성
    if (this.requiredTranslation()) {
      await this.loadGWidget()   // 스크립트 로드 (cb 순서 보장)
      await this.$nextTick()     // 타깃 DOM 보장
      this.ensureInstance()      // 인스턴스 1회 생성
      this.applyTranslateRule()  // 현재 상태 반영
    }
  },

  methods: {
    requiredTranslation () {
      const setting = this.$store.getters['common/getSetting']
      const langs = setting && setting.lang
      const isManualAllLangs = langs && langs.usable.length === langs.manual.length
      return !isManualAllLangs
    },
    /* -------------------------
       1) Google 위젯 스크립트 로드
    ------------------------- */
    loadGWidget() {
      return new Promise((resolve) => {
        if (window.google && window.google.translate) return resolve()
        window.googleTranslateElementInit = () => resolve() // cb 먼저 등록
        const s = document.createElement('script')
        s.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit'
        s.async = true
        document.head.appendChild(s)
      })
    },

    /* -------------------------
       2) 위젯 인스턴스 (1회만)
    ------------------------- */
    ensureInstance() {
      if (window.__gtReady) return
      // 초기엔 무조건 중립 쿠키(base->base)로 시작해 자동 번역 폭주 방지
      this.setGoogTrans(this.defaultLang, this.defaultLang, { clearFirst: true })

      /* global google */
      new window.google.translate.TranslateElement(
        { pageLanguage: this.defaultLang, autoDisplay: false },
        'google_translate_element'
      )
      window.__gtReady = true
    },

    /* -------------------------
       3) 적용 규칙
       - manual 또는 ln===defaultLang → 원문 유지
       - auto & ln!==defaultLang     → base→ln 트리거
    ------------------------- */
    applyTranslateRule() {
      const base = this.defaultLang
      const target = this.ln
      const isManual = (this.translateType === 'manual')

      if (isManual || target === base) {
        // 원문 유지: 쿠키 중립화 + 복원 트리거
        this.setGoogTrans(base, base, { clearFirst: true })
        this.clickRestoreButton(target)
        return
      }

      // 자동 번역: 쿠키 설정 + 타겟 언어로 트리거
      this.setGoogTrans(base, target, { clearFirst: true })
      this.comboTo(target)
    } ,

    /* -------------------------
       4) [자동번역일 때] 구글 콤보에 change 트리거
    ------------------------- */
    comboTo(val) {
      const fire = (combo) => {
        if (combo.value !== val) {
          combo.value = val
          combo.dispatchEvent(new Event('change', { bubbles: true }))
        }
      }
      const now = document.querySelector('.goog-te-combo')
      if (now) {
        fire(now)
        return
      }
      const id = setInterval(() => {
        const el = document.querySelector('.goog-te-combo')
        if (el) {
          fire(el)
          clearInterval(id)
        }
      }, 200)
      setTimeout(() => clearInterval(id), 5000)
    },
    /* -------------------------
       5) [직접번역일 때] 위젯에서 원문보기
    ------------------------- */
    clickRestoreButton (target) {
      // skiptranslate iframe 찾기
      const iframe = document.querySelector('iframe.skiptranslate[id$=".container"]')
      if (!iframe) {
        return console.warn('iframe not found')
      }

      // 내부 document 접근
      const doc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document)
      if (!doc) return console.warn('no iframe document')

      // "원본 표시" 버튼 찾기
      const restoreBtn = doc.querySelector('button[id$=".restore"]')
      if (!restoreBtn) return console.warn('restore button not found')

      // 클릭 트리거
      restoreBtn.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }))
    },


    /* -------------------------
       5) googtrans 쿠키 제어 (호스트 전용)
       - 서브도메인별 독립 동작
    ------------------------- */
    setGoogTrans(source, target, { clearFirst = false } = {}) {
      if (clearFirst) this.clearGoogTrans()
      // host-only + path=/ 로만 설정 (서브도메인 간섭 방지)
      const attrs = this.cookieAttrs({ path: '/' })
      document.cookie = `googtrans=/${source}/${target}${attrs}`
    },
    clearGoogTrans() {
      const exp = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
      // host-only 쿠키 제거
      document.cookie = `googtrans= ${exp}${this.cookieAttrs({ path: '/' })}`
      // 혹시 과거에 base 도메인으로 박아둔 쿠키가 있다면 한 번 더 제거(선택)
      const base = this.baseDomain()
      if (base) {
        document.cookie = `googtrans= ${exp}${this.cookieAttrs({ path: '/', domain: base })}`
      }
    },
    cookieAttrs({ path = '/', domain } = {}) {
      const segs = [` path=${path}`]
      if (domain) segs.push(` domain=${domain}`)
      if (location.protocol === 'https:') segs.push(' secure', ' samesite=Lax')
      return segs.join('')
    },
    baseDomain() {
      // center.istay.io → .istay.io
      const host = location.hostname.split('.')
      if (host.length >= 2) return '.' + host.slice(-2).join('.')
      return ''
    }
  }
}
</script>

<style lang="scss" scoped>
.poweredby {
  text-align: center;
  img {
    height: 16px;
  }
}
</style>
